import React, { useCallback, useEffect, useState } from 'react';
import companyUpdatesApi from '../services/companyUpdatesApi';
import { ActivityEntry } from './AdminPanel';
import Icon from './Icon';
import { OUTREACH_STAGE_LABELS } from './utils';
import { timeAgo } from '../utils/time';

/** One pending update: an editable summary (prefilled from the posted text), an optional
 * outreach-stage change, and Log/Dismiss - shaped like FindingCard's Accept/Reject. */
const PendingUpdateCard = ({ update, busy, onLog, onDismiss }) => {
  const [summary, setSummary] = useState(update.body);
  const [stage, setStage] = useState('');

  return (
    <li className="adm-event">
      <div className="adm-event__line">
        <span className="adm-event__who">{update.created_by}</span>
        <span className="adm-event__when">{timeAgo(update.created_at)}</span>
      </div>
      <textarea
        className="rm-input company-updates__summary"
        value={summary}
        onChange={(event) => setSummary(event.target.value)}
        rows={2}
        disabled={busy}
      />
      <select
        className="rm-input company-updates__stage"
        value={stage}
        onChange={(event) => setStage(event.target.value)}
        disabled={busy}
        aria-label="Change outreach stage"
      >
        <option value="">No change</option>
        {Object.entries(OUTREACH_STAGE_LABELS).map(([key, label]) => (
          <option key={key} value={key}>{label}</option>
        ))}
      </select>
      <div className="adm-finding__actions">
        <button
          type="button"
          className="rm-button rm-button--primary"
          disabled={busy}
          onClick={() => onLog(update, { summary, outreach_stage: stage || undefined })}
        >
          <Icon name="check" /> Log
        </button>
        <button type="button" className="rm-button rm-button--quiet" disabled={busy} onClick={() => onDismiss(update)}>
          Dismiss
        </button>
      </div>
    </li>
  );
};

/** Updates/activity for a company: post a free-text update, review pending ones into the activity
 * log below (optionally changing outreach stage), or dismiss them. */
const CompanyUpdates = ({ companyId, onDataChanged }) => {
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const [history, setHistory] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadPending = useCallback(() => {
    companyUpdatesApi.list(companyId)
      .then(data => setPending(data.updates || []))
      .catch(err => setError(err.message));
  }, [companyId]);

  const loadHistory = useCallback((beforeId) => companyUpdatesApi.history(companyId, beforeId)
    .then(page => {
      setNextBefore(page.next_before_id || null);
      return page.events || [];
    }), [companyId]);

  // Clear immediately on a company switch, so nothing from the previous company flashes
  useEffect(() => {
    setDraft('');
    setPending([]);
    setError(null);
    setHistory(null);
    setNextBefore(null);
    setHistoryError(null);
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    loadPending();
    loadHistory()
      .then(first => {
        if (!cancelled) setHistory(first);
      })
      .catch(err => {
        if (!cancelled) setHistoryError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, loadHistory, loadPending]);

  const showMoreHistory = async () => {
    setLoadingMore(true);
    try {
      const more = await loadHistory(nextBefore);
      setHistory(current => [...(current || []), ...more]);
    } catch (err) {
      setHistoryError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const post = async () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    setError(null);
    try {
      await companyUpdatesApi.create(companyId, body);
      setDraft('');
      loadPending();
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  const log = async (update, payload) => {
    setBusyId(update.id);
    setError(null);
    try {
      await companyUpdatesApi.log(update.id, payload);
      setPending(current => current.filter(item => item.id !== update.id));
      loadHistory().then(setHistory).catch(err => setHistoryError(err.message));
      onDataChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (update) => {
    setBusyId(update.id);
    setError(null);
    try {
      await companyUpdatesApi.dismiss(update.id);
      setPending(current => current.filter(item => item.id !== update.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="company-section company-updates">
      <h3>Updates &amp; activity</h3>
      {error && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}

      <div className="company-updates__compose">
        <textarea
          className="rm-input"
          placeholder="Add an update about this company..."
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={2}
        />
        <button type="button" className="rm-button rm-button--primary" disabled={posting || !draft.trim()} onClick={post}>
          {posting ? <Icon name="loader" spin /> : null} Post update
        </button>
      </div>

      {pending.length > 0 && (
        <ul className="adm-events company-updates__pending">
          {pending.map(update => (
            <PendingUpdateCard key={update.id} update={update} busy={busyId === update.id} onLog={log} onDismiss={dismiss} />
          ))}
        </ul>
      )}

      {historyError && <p className="company-panel-error" role="alert"><Icon name="alert-triangle" /> {historyError}</p>}
      {history === null && !historyError && <p className="placeholder-text"><Icon name="loader" spin /> Loading…</p>}
      {history && history.length === 0 && <p className="placeholder-text">No activity recorded yet.</p>}
      {history && history.length > 0 && (
        <ul className="adm-events">
          {history.map(event => <ActivityEntry key={event.id} event={event} />)}
        </ul>
      )}
      {nextBefore && (
        <button type="button" className="contact-history__more" onClick={showMoreHistory} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Show more'}
        </button>
      )}
    </div>
  );
};

export default CompanyUpdates;
